import { gql, useMutation } from "@apollo/client";
import { client } from "../apolloClient";

/**
 * useProjectApi custom hook for project API operations.
 * - Provides CRUD operations for projects using Apollo Client and GraphQL.
 * - Handles cache updates for project queries and mutations.
 * - Exposes helpers for reading/writing/updating projects in Apollo cache.
 * - Returns methods for fetching, creating, updating, and deleting projects.
 * - Ensures UI stays in sync with backend by updating Apollo cache after mutations.
 * - Supports project history, milestone history, comments, and status updates.
 */

// GraphQL Queries and Mutations
const PROJECT_QUERIES = {
  GET_PROJECTS: gql`
    query Query($getAll: Boolean) {
      GetProjects(getAll: $getAll) {
        project_id
        client_id
        client_name
        purchase_order_number
        project_name
        project_description
        project_type
        proposed_start_date
        proposed_end_date
        revised_start_date
        revised_end_date
        billability
        json_forms
        project_status_code
        project_priority_code
        risk
        communication
        budget_hours
        currency_code
        auto_approve
        is_active
        created_on
        updated_on
        project_pocs_mapping {
          project_poc_mapping_id
          client_poc_id
          project_id
          is_sponsor
          is_active
          created_on
          updated_on
          project_pocs {
            client_poc_id
            client_id
            poc_designation
            poc_country_id
            poc_name
            poc_email
            poc_mobile
            poc_country_name
            poc_business_unit
            is_active
            created_on
            updated_on
          }
        }
        primary_project_manager
        secondary_project_manager
        project_owner
        project_code
        project_model
        publish_type
        last_published_on
        is_default
      }
    }
  `,

  GET_PROJECTS_HISTORY: gql`
    query GetProjectHistory($projectId: Int!) {
      GetProjectHistory(project_id: $projectId) {
        project_id
        field_name
        old_value
        new_value
        changed_by
        created_on
      }
    }
  `,

  GET_PROJECTS_MILESTONE_HISTORY: gql`
    query GetProjectMilestoneHistory($projectId: Int!) {
      GetProjectMilestoneHistory(project_id: $projectId) {
        project_id
        milestone_id
        field_name
        old_value
        new_value
        changed_by
        changed_on
        milestone_name
      }
    }
  `,

  ADD_PROJECT: gql`
    mutation AddProject(
      $clientId: Int!
      $purchaseOrderNumber: String
      $projectName: String!
      $projectDescription: String!
      $projectType: ProjectType!
      $proposedStartDate: String!
      $proposedEndDate: String!
      $billability: Boolean
      $projectStatusCode: ProjectStatusCode!
      $projectPriorityCode: ProjectPriorityCode
      $risk: Risk
      $communication: Communication
      $budgetHours: Float!
      $currencyCode: CurrencyCode
      $revisedStartDate: String
      $revisedEndDate: String
      $jsonForms: JSON
      $isActive: Boolean
      $projectPocsMapping: [ProjectPocMappingInput]
      $primaryProjectManager: Int
      $secondaryProjectManager: Int
      $projectOwner: Int
      $projectModel: ProjectModel
      $auto_approve: Boolean
    ) {
      AddProject(
        client_id: $clientId
        purchase_order_number: $purchaseOrderNumber
        project_name: $projectName
        project_description: $projectDescription
        project_type: $projectType
        proposed_start_date: $proposedStartDate
        proposed_end_date: $proposedEndDate
        billability: $billability
        project_status_code: $projectStatusCode
        project_priority_code: $projectPriorityCode
        risk: $risk
        communication: $communication
        budget_hours: $budgetHours
        currency_code: $currencyCode
        revised_start_date: $revisedStartDate
        revised_end_date: $revisedEndDate
        json_forms: $jsonForms
        is_active: $isActive
        project_pocs_mapping: $projectPocsMapping
        primary_project_manager: $primaryProjectManager
        secondary_project_manager: $secondaryProjectManager
        project_owner: $projectOwner
        project_model: $projectModel
        auto_approve: $auto_approve
      ) {
        project_id
        client_id
        client_name
        purchase_order_number
        project_name
        project_description
        project_type
        proposed_start_date
        proposed_end_date
        revised_start_date
        revised_end_date
        billability
        json_forms
        project_status_code
        project_priority_code
        risk
        communication
        budget_hours
        currency_code
        is_active
        auto_approve
        created_on
        updated_on
        project_pocs_mapping {
          project_poc_mapping_id
          client_poc_id
          project_id
          is_sponsor
          is_active
          created_on
          updated_on
          project_pocs {
            client_poc_id
            client_id
            poc_designation
            poc_country_id
            poc_name
            poc_email
            poc_mobile
            poc_country_name
            poc_business_unit
            is_active
            created_on
            updated_on
          }
        }
        primary_project_manager
        secondary_project_manager
        project_owner
        project_code
        project_model
        publish_type
        last_published_on
        is_default
      }
    }
  `,

  UPDATE_PROJECT: gql`
    mutation UpdateProject(
      $projectId: ID!
      $clientId: Int
      $purchaseOrderNumber: String
      $projectName: String
      $projectDescription: String
      $projectType: ProjectType
      $proposedStartDate: String
      $proposedEndDate: String
      $revisedStartDate: String
      $revisedEndDate: String
      $billability: Boolean
      $jsonForms: JSON
      $projectStatusCode: ProjectStatusCode
      $projectPriorityCode: ProjectPriorityCode
      $risk: Risk
      $communication: Communication
      $budgetHours: Float
      $currencyCode: CurrencyCode
      $isActive: Boolean
      $projectPocsMapping: [ProjectPocMappingInput]
      $primaryProjectManager: Int
      $secondaryProjectManager: Int
      $projectOwner: Int
      $projectModel: ProjectModel
      $auto_approve: Boolean
    ) {
      UpdateProject(
        project_id: $projectId
        client_id: $clientId
        purchase_order_number: $purchaseOrderNumber
        project_name: $projectName
        project_description: $projectDescription
        project_type: $projectType
        proposed_start_date: $proposedStartDate
        proposed_end_date: $proposedEndDate
        revised_start_date: $revisedStartDate
        revised_end_date: $revisedEndDate
        billability: $billability
        json_forms: $jsonForms
        project_status_code: $projectStatusCode
        project_priority_code: $projectPriorityCode
        risk: $risk
        communication: $communication
        budget_hours: $budgetHours
        currency_code: $currencyCode
        is_active: $isActive
        project_pocs_mapping: $projectPocsMapping
        primary_project_manager: $primaryProjectManager
        secondary_project_manager: $secondaryProjectManager
        project_owner: $projectOwner
        project_model: $projectModel
        auto_approve: $auto_approve
      ) {
        project_id
        client_id
        client_name
        purchase_order_number
        project_name
        project_description
        project_type
        proposed_start_date
        proposed_end_date
        revised_start_date
        revised_end_date
        billability
        json_forms
        project_status_code
        project_priority_code
        risk
        communication
        budget_hours
        currency_code
        is_active
        auto_approve
        created_on
        updated_on
        project_pocs_mapping {
          project_poc_mapping_id
          client_poc_id
          project_id
          is_sponsor
          is_active
          created_on
          updated_on
          project_pocs {
            client_poc_id
            client_id
            poc_designation
            poc_country_id
            poc_name
            poc_email
            poc_mobile
            poc_country_name
            poc_business_unit
            is_active
            created_on
            updated_on
          }
        }
        primary_project_manager
        secondary_project_manager
        project_owner
        project_code
        project_model
        publish_type
        last_published_on
        is_default
      }
    }
  `,

  DELETE_PROJECT: gql`
    mutation DeleteProject($project_id: ID!) {
      DeleteProject(project_id: $project_id) {
        project_id
      }
    }
  `,
  GET_PROJECT_BY_ID: gql`
    query GetProjectById($project_id: ID!) {
      GetProjectById(project_id: $project_id) {
        project_id
        client_id
        client_name
        purchase_order_number
        project_name
        project_description
        project_type
        proposed_start_date
        proposed_end_date
        revised_start_date
        revised_end_date
        billability
        json_forms
        project_status_code
        project_priority_code
        risk
        communication
        budget_hours
        currency_code
        is_active
        created_on
        updated_on
        primary_project_manager
        secondary_project_manager
        project_owner
        project_code
        project_model
        publish_type
        last_published_on
        auto_approve
        is_default
      }
    }
  `,
  GET_PROJECTS_BY_RESOURCE_ID: gql`
    query GetProjectsByResourceId($resourceId: Int!) {
      GetProjectsByResourceId(resource_id: $resourceId) {
        project_id
        client_id
        client_name
        purchase_order_number
        project_name
        project_description
        project_type
        proposed_start_date
        proposed_end_date
        revised_start_date
        revised_end_date
        billability
        json_forms
        project_status_code
        project_priority_code
        risk
        communication
        budget_hours
        currency_code
        is_active
        created_on
        updated_on
        auto_approve
        project_pocs_mapping {
          project_poc_mapping_id
          client_poc_id
          project_id
          is_sponsor
          is_active
          created_on
          updated_on
          project_pocs {
            client_poc_id
            client_id
            poc_designation
            poc_country_id
            poc_name
            poc_email
            poc_mobile
            poc_country_name
            poc_business_unit
            is_active
            created_on
            updated_on
          }
        }
        primary_project_manager
        secondary_project_manager
        project_owner
        project_code
        project_model
        publish_type
        last_published_on
        is_default
      }
    }
  `,

  GET_PROJECT_MANAGER_BY_RESOURCE_ID: gql`
    query GetProjectManagerByResourceId($resourceId: Int!, $roleId: Int!) {
      GetProjectManagerByResourceId(
        resource_id: $resourceId
        role_id: $roleId
      ) {
        project_id
        client_id
        client_name
        purchase_order_number
        project_name
        project_description
        project_type
        proposed_start_date
        proposed_end_date
        revised_start_date
        revised_end_date
        billability
        json_forms
        project_status_code
        project_priority_code
        risk
        communication
        budget_hours
        currency_code
        is_active
        auto_approve
        created_on
        updated_on
        project_pocs_mapping {
          project_poc_mapping_id
          client_poc_id
          project_id
          is_sponsor
          is_active
          created_on
          updated_on
          project_pocs {
            client_poc_id
            client_id
            poc_designation
            poc_country_id
            poc_name
            poc_email
            poc_mobile
            poc_country_name
            poc_business_unit
            is_active
            created_on
            updated_on
          }
        }
        primary_project_manager
        secondary_project_manager
        project_code
        project_model
        publish_type
        last_published_on
        is_default
      }
    }
  `,

  GET_PROJECT_COMMENT_HISTORY: gql`
    query GetProjectCommentHistory($project_id: ID!, $moduleComment: String) {
      GetProjectCommentHistory(
        project_id: $project_id
        module_comment: $moduleComment
      ) {
        id
        project_id
        resource_id
        comment
        type
        created_on
        updated_on
        resource_name
      }
    }
  `,

  UPDATE_PROJECT_STATUS: gql`
    mutation UpdateProjectStatus(
      $projectId: ID!
      $publishType: PublishedType!
      $type: String
    ) {
      UpdateProjectStatus(
        project_id: $projectId
        publish_type: $publishType
        type: $type
      ) {
        project_id
        client_id
        client_name
        purchase_order_number
        project_name
        project_description
        project_type
        proposed_start_date
        proposed_end_date
        revised_start_date
        revised_end_date
        billability
        json_forms
        project_status_code
        project_priority_code
        risk
        communication
        budget_hours
        currency_code
        is_active
        created_on
        updated_on
        project_pocs_mapping {
          project_poc_mapping_id
          client_poc_id
          project_id
          is_sponsor
          is_active
          created_on
          updated_on
          project_pocs {
            client_poc_id
            client_id
            poc_designation
            poc_country_id
            poc_name
            poc_email
            poc_mobile
            poc_country_name
            poc_business_unit
            is_active
            created_on
            updated_on
          }
        }
        primary_project_manager
        secondary_project_manager
        project_owner
        project_code
        project_model
        auto_approve
        publish_type
        last_published_on
        is_default
      }
    }
  `,

  ADD_PROJECT_COMMENT: gql`
    mutation AddProjectComment(
      $projectId: ID!
      $comment: String!
      $type: CommentType!
      $moduleComment: ModuleComment
    ) {
      AddProjectComment(
        project_id: $projectId
        comment: $comment
        type: $type
        module_comment: $moduleComment
      ) {
        id
        project_id
        resource_id
        comment
        type
        module_comment
        created_on
        updated_on
        resource_name
      }
    }
  `,

  GET_PROJECT_ACTUAL_HOURS: gql`
    query GetProjectActualHours($projectId: ID!) {
      GetProjectActualHours(project_id: $projectId) {
        project_id
        project_budget_hours
        budget_hours_percentage
        remaining_hours_percentage
        total_actual_hours
        total_estimated_hours
      }
    }
  `,
};

// Cache operations
const cacheOperations = {
  readProjects: (getAll) => {
    try {
      const { GetProjects = [] } =
        client.readQuery({
          variables: { getAll },
          query: PROJECT_QUERIES.GET_PROJECTS,
        }) || {};
      return GetProjects;
    } catch {
      return [];
    }
  },
  readProjectHistory: (projectId) => {
    try {
      const { GetProjectHistory = [] } =
        client.readQuery({
          variables: { projectId },
          query: PROJECT_QUERIES.GET_PROJECTS_HISTORY,
        }) || {};
      return GetProjectHistory;
    } catch {
      return [];
    }
  },
  readProjectMilestoneHistory: (projectId) => {
    try {
      const { GetProjectMilestoneHistory = [] } =
        client.readQuery({
          variables: { projectId },
          query: PROJECT_QUERIES.GET_PROJECTS_MILESTONE_HISTORY,
        }) || {};
      return GetProjectMilestoneHistory;
    } catch {
      return [];
    }
  },
  writeProjects: (projects, getAll) => {
    client.writeQuery({
      variables: { getAll },
      query: PROJECT_QUERIES.GET_PROJECTS,
      data: { GetProjects: projects },
    });
  },
  writeProjectsMilestoneHistory: (projectsMilestoneHistory, getAll) => {
    client.writeQuery({
      variables: { getAll },
      query: PROJECT_QUERIES.GET_PROJECTS_MILESTONE_HISTORY,
      data: { GetProjectMilestoneHistory: projectsMilestoneHistory },
    });
  },
};

export const useProjectApi = () => {
  const [addProjectMutation] = useMutation(PROJECT_QUERIES.ADD_PROJECT, {
    update(cache, { data: { AddProject } }) {
      try {
        const isSuperAdmin = client.cache.config.isSuperAdmin;
        const existingData = cache.readQuery({
          query: PROJECT_QUERIES.GET_PROJECTS,
          variables: { getAll: isSuperAdmin },
        });
        if (existingData) {
          cache.writeQuery({
            query: PROJECT_QUERIES.GET_PROJECTS,
            variables: { getAll: isSuperAdmin },
            data: {
              GetProjects: [AddProject, ...existingData.GetProjects],
            },
          });
        }
      } catch (error) {
        console.error("Cache update error:", error);
      }
    },
  });

  const [updateProjectMutation] = useMutation(PROJECT_QUERIES.UPDATE_PROJECT, {
    update(cache, { data: { UpdateProject } }) {
      try {
        const isSuperAdmin = client.cache.config.isSuperAdmin;
        const existingData = cache.readQuery({
          query: PROJECT_QUERIES.GET_PROJECTS,
          variables: { getAll: isSuperAdmin },
        });
        if (existingData) {
          const newData = existingData.GetProjects.map((project) =>
            project.project_id === UpdateProject.project_id
              ? UpdateProject
              : project
          );
          cache.writeQuery({
            query: PROJECT_QUERIES.GET_PROJECTS,
            variables: { getAll: isSuperAdmin },
            data: { GetProjects: newData },
          });
        }
      } catch (error) {
        console.error("Error updating cache after updating project:", error);
      }
    },
  });

  const [deleteProjectMutation] = useMutation(PROJECT_QUERIES.DELETE_PROJECT, {
    update(cache, { data: { DeleteProject } }) {
      try {
        const isSuperAdmin = client.cache.config.isSuperAdmin;
        const existingData = cache.readQuery({
          query: PROJECT_QUERIES.GET_PROJECTS,
          variables: { getAll: isSuperAdmin },
        });
        if (existingData) {
          cache.writeQuery({
            query: PROJECT_QUERIES.GET_PROJECTS,
            variables: { getAll: isSuperAdmin },
            data: {
              GetProjects: existingData.GetProjects.filter(
                (project) => project.project_id !== DeleteProject.project_id
              ),
            },
          });
        }
      } catch (error) {
        console.error("Error updating cache after deleting project:", error);
      }
    },
  });

  const [updateProjectStatusMutation] = useMutation(
    PROJECT_QUERIES.UPDATE_PROJECT_STATUS,
    {
      update(cache, { data: { UpdateProjectStatus } }) {
        try {
          const isSuperAdmin = client.cache.config.isSuperAdmin;
          const existingData = cache.readQuery({
            query: PROJECT_QUERIES.GET_PROJECTS,
            variables: { getAll: isSuperAdmin },
          });
          if (existingData) {
            const newData = existingData.GetProjects.map((project) =>
              project.project_id === UpdateProjectStatus.project_id
                ? UpdateProjectStatus
                : project
            );
            cache.writeQuery({
              query: PROJECT_QUERIES.GET_PROJECTS,
              variables: { getAll: isSuperAdmin },
              data: { GetProjects: newData },
            });
          }
        } catch (error) {
          console.error(
            "Error updating cache after updating project status:",
            error
          );
        }
      },
    }
  );

  const [addProjectCommentMutation] = useMutation(
    PROJECT_QUERIES.ADD_PROJECT_COMMENT,
    {
      update(cache, { data: { AddProjectComment } }) {
        try {
          const existingData = cache.readQuery({
            query: PROJECT_QUERIES.GET_PROJECT_COMMENT_HISTORY,
            variables: { project_id: AddProjectComment.project_id.toString() },
          });

          if (existingData) {
            cache.writeQuery({
              query: PROJECT_QUERIES.GET_PROJECT_COMMENT_HISTORY,
              variables: {
                project_id: AddProjectComment.project_id.toString(),
              },
              data: {
                GetProjectCommentHistory: [
                  ...existingData.GetProjectCommentHistory,
                  AddProjectComment,
                ],
              },
            });
          }
        } catch (error) {
          console.error("Cache update error:", error);
        }
      },
    }
  );

  const getProjects = async (isSuperAdmin) => {
    try {
      console.log("Fetching projects...");
      const { data } = await client.query({
        variables: { getAll: isSuperAdmin },
        query: PROJECT_QUERIES.GET_PROJECTS,
        fetchPolicy: "network-only",
        nextFetchPolicy: "cache-first",
      });

      if (!data?.GetProjects) {
        throw new Error("No projects data received");
      }

      return { success: true, data: data.GetProjects };
    } catch (error) {
      console.error("Project fetch error:", error);
      return { success: false, error: error.message };
    }
  };

  const getProjectHistory = async (projectId) => {
    try {
      console.log("Fetching project history...");
      const { data } = await client.query({
        variables: { projectId: parseInt(projectId) },
        query: PROJECT_QUERIES.GET_PROJECTS_HISTORY,
        fetchPolicy: "network-only",
        nextFetchPolicy: "cache-first",
      });

      if (!data?.GetProjectHistory) {
        throw new Error("No projects history data received");
      }

      return { success: true, data: data.GetProjectHistory };
    } catch (error) {
      console.error("Project history fetch error:", error);
      return { success: false, error: error.message };
    }
  };

  const getProjectMilestoneHistory = async (projectId) => {
    try {
      console.log("Fetching project milestone history...");
      const { data } = await client.query({
        variables: { projectId: parseInt(projectId) },
        query: PROJECT_QUERIES.GET_PROJECTS_MILESTONE_HISTORY,
        fetchPolicy: "network-only",
        nextFetchPolicy: "cache-first",
      });

      if (!data?.GetProjectMilestoneHistory) {
        throw new Error("No projects milestone history data received");
      }

      return { success: true, data: data.GetProjectMilestoneHistory };
    } catch (error) {
      console.error("Project milestone history fetch error:", error);
      return { success: false, error: error.message };
    }
  };

  const getProjectById = async (projectId) => {
    try {
      const { data } = await client.query({
        query: PROJECT_QUERIES.GET_PROJECT_BY_ID,
        variables: { project_id: parseInt(projectId) },
        fetchPolicy: "network-only",
        nextFetchPolicy: "cache-first",
      });
      if (!data?.GetProjectById) {
        throw new Error("No projects data received");
      }

      return { success: true, data: data.GetProjectById };
    } catch (error) {
      console.error("Project fetch error:", error);
      return { success: false, error: error.message };
    }
  };

  const createProject = async (formData, isSuperAdmin) => {
    try {
      client.cache.config.isSuperAdmin = isSuperAdmin;
      const { data } = await addProjectMutation({
        variables: {
          clientId: parseInt(formData.client_id),
          projectName: formData.project_name,
          purchaseOrderNumber: formData.purchase_order_number,
          projectDescription: formData.project_description,
          projectType: formData.project_type,
          proposedStartDate: formData.proposed_start_date,
          proposedEndDate: formData.proposed_end_date,
          revisedStartDate: formData.revised_start_date || null,
          revisedEndDate: formData.revised_end_date || null,
          billability: formData.billability,
          projectPriorityCode: formData.project_priority_code,
          risk: formData.risk,
          communication: formData.communication,
          budgetHours: parseFloat(formData.budget_hours),
          currencyCode: formData.currency_code,
          isActive: formData.is_active,
          projectStatusCode: formData.project_status_code || "PENDING",
          primaryProjectManager: formData?.primary_project_manager
            ? parseInt(formData?.primary_project_manager)
            : formData?.primary_project_manager,
          secondaryProjectManager: formData?.secondary_project_manager
            ? parseInt(formData?.secondary_project_manager)
            : null,
          projectOwner: formData?.project_owner
            ? parseInt(formData?.project_owner)
            : null,
          projectPocsMapping: formData.project_pocs_mapping,
          projectModel: formData.project_model,
          auto_approve: formData.auto_approve,
        },
      });

      if (data?.AddProject) {
        return { success: true, data: data?.AddProject };
      } else {
        throw new Error("Failed to create project");
      }
    } catch (error) {
      console.error("Error creating project:", error);
      return { success: false, error: error.message };
    }
  };

  const updateProject = async (projectId, formData, isSuperAdmin) => {
    try {
      client.cache.config.isSuperAdmin = isSuperAdmin;
      const { data } = await updateProjectMutation({
        variables: {
          projectId: projectId.toString(),
          clientId: parseInt(formData.client_id),
          purchaseOrderNumber: formData.purchase_order_number,
          projectName: formData.project_name,
          projectDescription: formData.project_description,
          projectType: formData.project_type,
          proposedStartDate: formData.proposed_start_date,
          proposedEndDate: formData.proposed_end_date,
          revisedStartDate: formData.revised_start_date || null,
          revisedEndDate: formData.revised_end_date || null,
          billability: formData.billability,
          projectPriorityCode: formData.project_priority_code,
          projectStatusCode: formData.project_status_code || "PENDING",
          risk: formData.risk,
          communication: formData.communication,
          budgetHours: parseFloat(formData.budget_hours),
          currencyCode: formData.currency_code,
          isActive: formData.is_active,
          primaryProjectManager: formData?.primary_project_manager
            ? parseInt(formData?.primary_project_manager)
            : formData?.primary_project_manager,
          secondaryProjectManager: formData?.secondary_project_manager
            ? parseInt(formData?.secondary_project_manager)
            : null,
          projectOwner: formData?.project_owner
            ? parseInt(formData?.project_owner)
            : null,
          projectPocsMapping: formData.project_pocs_mapping,
          projectModel: formData.project_model,
          auto_approve: formData.auto_approve,
        },
      });

      if (data?.UpdateProject) {
        return { success: true, data: data.UpdateProject };
      } else {
        throw new Error("Failed to update project");
      }
    } catch (error) {
      console.error("Error updating project:", error);
      return { success: false, error: error.message };
    }
  };

  const deleteProject = async (projectId, isSuperAdmin) => {
    try {
      client.cache.config.isSuperAdmin = isSuperAdmin;
      const { data } = await deleteProjectMutation({
        variables: { project_id: projectId.toString() },
      });

      return { success: true, data: data.DeleteProject };
    } catch (error) {
      console.error("Error deleting project:", error);
      return { success: false, error: error.message };
    }
  };

  const getProjectsByResourceId = async (resourceId) => {
    try {
      const { data } = await client.query({
        query: PROJECT_QUERIES.GET_PROJECTS_BY_RESOURCE_ID,
        variables: { resourceId: parseInt(resourceId) },
        fetchPolicy: "network-only",
        nextFetchPolicy: "cache-first",
      });

      if (!data?.GetProjectsByResourceId) {
        throw new Error("No projects data received for the given resource ID");
      }

      return { success: true, data: data.GetProjectsByResourceId };
    } catch (error) {
      console.error("Error fetching projects by resource ID:", error);
      return { success: false, error: error.message };
    }
  };
  const getProjectManagerByResourceId = async (resourceId, roleId) => {
    try {
      const { data } = await client.query({
        query: PROJECT_QUERIES.GET_PROJECT_MANAGER_BY_RESOURCE_ID,
        variables: {
          resourceId: parseInt(resourceId),
          roleId: parseInt(roleId),
        },
        fetchPolicy: "network-only",
        nextFetchPolicy: "cache-first",
      });

      if (!data?.GetProjectManagerByResourceId) {
        throw new Error("No projects data received for the given resource ID");
      }

      return { success: true, data: data.GetProjectManagerByResourceId };
    } catch (error) {
      console.error("Error fetching projects by resource ID:", error);
      return { success: false, error: error.message };
    }
  };

  const getProjectCommentHistory = async (
    projectId,
    moduleComment = "PROJECT"
  ) => {
    try {
      const { data } = await client.query({
        query: PROJECT_QUERIES.GET_PROJECT_COMMENT_HISTORY,
        variables: {
          project_id: projectId.toString(),
          moduleComment: moduleComment,
        },
        fetchPolicy: "network-only",
        nextFetchPolicy: "cache-first",
      });

      if (!data?.GetProjectCommentHistory) {
        throw new Error("No comment history data received");
      }

      return { success: true, data: data.GetProjectCommentHistory };
    } catch (error) {
      console.error("Error fetching project comment history:", error);
      return { success: false, error: error.message };
    }
  };

  const updateProjectStatus = async (
    projectId,
    publishType,
    comments = [],
    isSuperAdmin,
    type = "MANAGER"
  ) => {
    try {
      client.cache.config.isSuperAdmin = isSuperAdmin;
      const { data } = await updateProjectStatusMutation({
        variables: {
          projectId: projectId.toString(),
          publishType: publishType,

          type: type,
        },
      });

      if (data?.UpdateProjectStatus) {
        return { success: true, data: data.UpdateProjectStatus };
      } else {
        throw new Error("Failed to update project publish status");
      }
    } catch (error) {
      console.error("Error updating project publish status:", error);
      return { success: false, error: error.message };
    }
  };

  const addProjectComment = async (
    projectId,
    comment,
    type = "MANAGER",
    moduleComment = "PROJECT"
  ) => {
    try {
      const { data } = await addProjectCommentMutation({
        variables: {
          projectId: projectId.toString(),
          comment,
          type,
          moduleComment,
        },
      });

      if (data?.AddProjectComment) {
        return { success: true, data: data.AddProjectComment };
      } else {
        throw new Error("Failed to add project comment");
      }
    } catch (error) {
      console.error("Error adding project comment:", error);
      return { success: false, error: error.message };
    }
  };

  const getProjectActualHours = async (projectId) => {
    try {
      const { data } = await client.query({
        query: PROJECT_QUERIES.GET_PROJECT_ACTUAL_HOURS,
        variables: { projectId: projectId.toString() },
        fetchPolicy: "network-only",
        nextFetchPolicy: "cache-first",
      });

      if (!data?.GetProjectActualHours) {
        throw new Error("No project hours data received");
      }

      return { success: true, data: data.GetProjectActualHours };
    } catch (error) {
      console.error("Error fetching project hours:", error);
      return { success: false, error: error.message };
    }
  };

  return {
    getProjects,
    getProjectById,
    createProject,
    updateProject,
    deleteProject,
    cacheOperations,
    getProjectsByResourceId,
    getProjectManagerByResourceId,
    updateProjectStatus,
    getProjectCommentHistory,
    getProjectHistory,
    getProjectMilestoneHistory,
    addProjectComment,
    getProjectActualHours,
  };
};
